import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import ButtonBox from "component/ButtonBox/ButtonBox";
import { linkAuthRoute, linkPageAudioAnalysis } from "routes";
import useAudioWorkflowStatus, {
  REQUIRED_COMPLETED_STEPS,
  STEP_UI,
} from "./useAudioWorkflowStatus";

interface WorkflowStepsViewProps {
  /** Audio url identifying the tracked workflow; feeds the `/audio/progress` long-poll. */
  audioUrl: string;
  /** Optional kickoff message returned by the upload endpoint. */
  processingStartMessage?: string;
  onClose: () => void;
}

/**
 * Dialog body showing the per-step status of one audio workflow. Mounting this
 * component starts the 3s `/audio/progress` poll (via useAudioWorkflowStatus);
 * unmounting it — closing the dialog — stops it.
 */
const WorkflowStepsView = ({ audioUrl, processingStartMessage, onClose }: WorkflowStepsViewProps) => {
  const navigate = useNavigate();
  const progressListRef = useRef<HTMLDivElement | null>(null);

  const {
    pipelineProgress,
    completedCount,
    progressLoading,
    fileId,
    processedUrl,
    status: workflowStatus,
  } = useAudioWorkflowStatus(audioUrl);

  const analysisRedirectQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (fileId) {
      params.set("file-id", fileId);
      // PageAudioAnalysis also reads audioId; keep it aligned with file id for backend lookups.
      params.set("audioId", fileId);
    }
    if (audioUrl) {
      params.set("audio", audioUrl);
    }
    if (processedUrl) {
      params.set("processed-audio", processedUrl);
    }
    // Keep a non-empty document id since analysis endpoint expects this query parameter.
    params.set("documentId", "default");
    return params.toString();
  }, [fileId, audioUrl, processedUrl]);

  const canNavigateToAudioAnalysis = workflowStatus === "succeeded" && !!fileId && !!audioUrl;
  const isRunning = workflowStatus === "running" || workflowStatus === "pending";

  useEffect(() => {
    const el = progressListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [completedCount, canNavigateToAudioAnalysis]);

  return (
    <>
      <div className="flex min-h-0 flex-col overflow-auto rounded-lg border border-gray-200 bg-white">
        <div className="flex shrink-0 items-center justify-between rounded-t-lg border-b bg-gray-50 px-3 py-2">
          <span className="text-sm font-semibold text-gray-700">
            Analysis & PD Report Progress ({completedCount}/{REQUIRED_COMPLETED_STEPS})
          </span>
          {isRunning && progressLoading && (
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <ProgressSpinner style={{ width: "14px", height: "14px" }} strokeWidth="8" />
              Updating...
            </div>
          )}
          {workflowStatus === "succeeded" && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-green-700 uppercase">
              Succeeded
            </span>
          )}
          {workflowStatus === "failed" && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-red-700 uppercase">
              Failed
            </span>
          )}
        </div>
        {processingStartMessage && (
          <div className="shrink-0 border-b border-gray-100 bg-white px-3 py-2">
            <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
              {processingStartMessage}
            </div>
          </div>
        )}
        {workflowStatus === "failed" && (
          <div className="shrink-0 border-b border-gray-100 bg-white px-3 py-2">
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              The workflow stopped because a step failed. Please re-upload the audio to try again.
            </div>
          </div>
        )}
        <div ref={progressListRef} className="max-h-[min(45vh,420px)] flex-1 overflow-auto">
          <div className="min-h-0 space-y-2 p-3 break-words">
            {pipelineProgress.map((step) => {
              const ui = STEP_UI[step.state];
              return (
                <div
                  key={step.key}
                  className={`flex items-center gap-3 rounded border px-3 py-2 text-sm ${ui.row}`}
                >
                  <span className="w-4 shrink-0 text-center font-semibold">{ui.icon}</span>
                  <span className="flex-1">{step.label}</span>
                  <span className="shrink-0 text-[11px] font-semibold tracking-wide uppercase">
                    {ui.badge}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-1.5">
        <ButtonBox
          label={isRunning ? "Run in background" : "Close"}
          variant="secondary"
          size="small"
          onClick={onClose}
        />
        {canNavigateToAudioAnalysis && (
          <ButtonBox
            label="Go to Audio Analysis"
            variant="primary"
            size="small"
            onClick={() =>
              navigate(linkAuthRoute + linkPageAudioAnalysis + `?${analysisRedirectQuery}`)
            }
            className="!bg-purple-600 hover:!bg-purple-700"
          />
        )}
      </div>
    </>
  );
};

export default WorkflowStepsView;
